/**
 * IntakeLinkListPage — page racine `/intake-links` (Chunk 9.N.3.A — broker UI).
 *
 * Fonctionnalités :
 *   - Liste paginée des liens d'intake du tenant courant (cursor keyset).
 *   - Filtre status (pills 5 options : all / active / expired / revoked / quota_reached).
 *   - Création d'un nouveau lien (CreateIntakeLinkDialog → IntakeTokenRevealDialog).
 *   - Révocation d'un lien (RevokeIntakeLinkDialog).
 *
 * États rendus distincts (CLAUDE.md §3 : « states never shared ») :
 *   - loading       — spinner inline + table skeleton-friendly (placeholderData)
 *   - error         — bandeau rouge bilingue + retry
 *   - empty         — illustration KollectiviaMark + microcopy + CTA primary
 *   - empty-filtered — sub-état si filtre status sans résultat
 *   - filled        — table responsive (table ≥ lg, cartes empilées < lg)
 *
 * Responsive (décision PO 9.N.3.A) :
 *   - ≥ lg : table avec 7 colonnes (project, status, created, expires, submissions, created_by, actions)
 *   - <  lg : cartes empilées (markup conditionnel Tailwind, pas de composant Card séparé)
 *
 * Sécurité :
 *   - Aucun `console.log` du token bundle. Le bundle reste dans `useState` local
 *     pendant la durée du IntakeTokenRevealDialog ; détruit (`null`) à la fermeture.
 *   - Rendu de `intake_url` UNIQUEMENT dans IntakeTokenRevealDialog — pas dans la
 *     liste, pas dans les logs, pas dans le DOM ailleurs.
 */

import type { IntakeLinkBundle, IntakeLinkStatusFilter } from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Pagination, usePagination } from "../../components/Pagination";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useIntakeLinks, type IntakeLink } from "../../lib/queries/intakeLinks";

import { CreateIntakeLinkDialog } from "./_parts/CreateIntakeLinkDialog";
import { IntakeLinkStatusBadge } from "./_parts/IntakeLinkStatusBadge";
import { IntakeLinkStatusFilter as StatusFilterPills } from "./_parts/IntakeLinkStatusFilter";
import { IntakeTokenRevealDialog } from "./_parts/IntakeTokenRevealDialog";
import { RevokeIntakeLinkDialog } from "./_parts/RevokeIntakeLinkDialog";

const TABLE_ID = "intake-links-table";

export function IntakeLinkListPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [status, setStatus] = useState<IntakeLinkStatusFilter>("all");
  const pagination = usePagination();

  const { data, isLoading, isFetching, error } = useIntakeLinks({
    cursor: pagination.cursor,
    status,
  });

  // Dialogs state
  const [createOpen, setCreateOpen] = useState(false);
  const [revealBundle, setRevealBundle] = useState<IntakeLinkBundle | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{
    id: string;
    projectTitle: string | null;
  } | null>(null);

  const onStatusChange = (next: IntakeLinkStatusFilter) => {
    setStatus(next);
    pagination.reset();
  };

  const items = data?.items ?? [];
  const isEmpty = !isLoading && items.length === 0 && !error;
  const hasFilter = status !== "all";

  return (
    <div className="mx-auto max-w-6xl">
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            {t("intake_links.list.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("intake_links.list.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M8 3v10M3 8h10"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
          {t("intake_links.list.create_cta")}
        </button>
      </header>

      {/* Filtre status */}
      <div className="mt-6">
        <StatusFilterPills value={status} onChange={onStatusChange} controlsId={TABLE_ID} />
      </div>

      {/* Erreur API (bandeau global) */}
      {error && (
        <div
          role="alert"
          className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {error instanceof ApiException
            ? getApiErrorMessage(error, lang) || t("errors.generic")
            : t("errors.generic")}
        </div>
      )}

      {/* Empty state — distinct selon filtré ou non */}
      {isEmpty && (
        <EmptyState
          filtered={hasFilter}
          onCreate={() => setCreateOpen(true)}
          onClearFilter={() => onStatusChange("all")}
        />
      )}

      {/* Tableau (≥ lg) + cartes empilées (< lg) */}
      {!isEmpty && (
        <div className="mt-4" id={TABLE_ID}>
          {/* Loading state initial — pas de data du tout */}
          {isLoading && !data ? (
            <LoadingState />
          ) : (
            <>
              <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white lg:block">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-medium">
                        {t("intake_links.list.columns.project")}
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        {t("intake_links.list.columns.status")}
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        {t("intake_links.list.columns.created_at")}
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        {t("intake_links.list.columns.expires_at")}
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        {t("intake_links.list.columns.submissions")}
                      </th>
                      <th scope="col" className="px-4 py-3 text-right font-medium">
                        <span className="sr-only">{t("intake_links.list.columns.actions")}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((link) => (
                      <tr key={link.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <Link
                            to={`/intake-links/${link.id}`}
                            className="text-brand-700 focus-visible:ring-brand-500 block max-w-xs truncate rounded-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                          >
                            {link.project?.title ?? (
                              <span className="text-slate-400">
                                {t("intake_links.list.project_deleted")}
                              </span>
                            )}
                          </Link>
                          <LinkIdentity link={link} />
                        </td>
                        <td className="px-4 py-3">
                          <IntakeLinkStatusBadge status={link.status} />
                        </td>
                        <td className="px-4 py-3 text-xs tabular-nums text-slate-500">
                          {formatDate(link.created_at, lang)}
                        </td>
                        <td className="px-4 py-3 text-xs tabular-nums text-slate-500">
                          {link.expires_at ? (
                            formatDate(link.expires_at, lang)
                          ) : (
                            <span className="text-slate-400">
                              {t("intake_links.list.expires_never")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs tabular-nums text-slate-700">
                          {formatSubmissions(link)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <RevokeButton link={link} onRevoke={setRevokeTarget} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Cartes empilées (< lg) — pattern « card link overlay » :
                  <Link> en absolute inset-0 (full bleed) couvre la zone
                  cliquable de la carte ; bouton Révoquer en relative z-10
                  reste au-dessus du link et capture ses propres clics
                  (sans naviguer). a11y : aria-label sur le Link explicite
                  « Voir le détail du lien » + Tab order naturel
                  (Link puis Revoke). */}
              <ul className="space-y-3 lg:hidden">
                {items.map((link) => (
                  <li
                    key={link.id}
                    className="focus-within:ring-brand-500 relative rounded-xl border border-slate-200 bg-white p-4 shadow-sm focus-within:ring-2 focus-within:ring-offset-2"
                  >
                    <Link
                      to={`/intake-links/${link.id}`}
                      aria-label={link.project?.title ?? t("intake_links.list.project_deleted")}
                      className="absolute inset-0 rounded-xl focus:outline-none"
                    >
                      <span className="sr-only">
                        {link.project?.title ?? t("intake_links.list.project_deleted")}
                      </span>
                    </Link>
                    <div className="relative flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-brand-700 truncate text-sm font-medium">
                          {link.project?.title ?? (
                            <span className="text-slate-400">
                              {t("intake_links.list.project_deleted")}
                            </span>
                          )}
                        </div>
                        <LinkIdentity link={link} />
                        <div className="mt-1">
                          <IntakeLinkStatusBadge status={link.status} />
                        </div>
                      </div>
                      {/* z-10 + relative pour être au-dessus du <Link> overlay */}
                      <div className="relative z-10">
                        <RevokeButton link={link} onRevoke={setRevokeTarget} />
                      </div>
                    </div>
                    <dl className="relative mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <dt className="text-slate-400">
                          {t("intake_links.list.columns.created_at")}
                        </dt>
                        <dd className="tabular-nums text-slate-700">
                          {formatDate(link.created_at, lang)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-slate-400">
                          {t("intake_links.list.columns.expires_at")}
                        </dt>
                        <dd className="tabular-nums text-slate-700">
                          {link.expires_at
                            ? formatDate(link.expires_at, lang)
                            : t("intake_links.list.expires_never")}
                        </dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-slate-400">
                          {t("intake_links.list.columns.submissions")}
                        </dt>
                        <dd className="tabular-nums text-slate-700">{formatSubmissions(link)}</dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>

              <div className="mt-4">
                <Pagination
                  pageNumber={pagination.pageNumber}
                  hasPrev={pagination.hasPrev}
                  hasNext={!!data?.next_cursor}
                  onPrev={pagination.prev}
                  onNext={() => pagination.next(data?.next_cursor ?? null)}
                  disabled={isFetching}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Dialogs */}
      <CreateIntakeLinkDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(bundle) => {
          // Anti-leak strict : aucun console.log du bundle ici.
          setCreateOpen(false);
          setRevealBundle(bundle);
        }}
      />
      <IntakeTokenRevealDialog
        open={!!revealBundle}
        bundle={revealBundle}
        onClose={() => setRevealBundle(null)}
      />
      <RevokeIntakeLinkDialog
        open={!!revokeTarget}
        intakeLinkId={revokeTarget?.id ?? null}
        projectTitle={revokeTarget?.projectTitle ?? null}
        onClose={() => setRevokeTarget(null)}
        onRevoked={() => setRevokeTarget(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sous-composants & helpers
// ---------------------------------------------------------------------------

function RevokeButton({
  link,
  onRevoke,
}: {
  link: IntakeLink;
  onRevoke: (target: { id: string; projectTitle: string | null }) => void;
}) {
  const { t } = useTranslation();
  const disabled = link.status === "revoked";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        // 9.N.3.B — stopPropagation + preventDefault défense en profondeur
        // pour que le click NE déclenche PAS la navigation parente vers
        // /intake-links/:id (la cellule projet desktop est <Link> + la
        // carte mobile a un <Link> overlay absolute inset-0). Le z-index
        // du wrapper est censé absorber les clics, mais en cas de
        // bubbling navigateur résiduel ça garantit l'isolation.
        e.stopPropagation();
        e.preventDefault();
        onRevoke({ id: link.id, projectTitle: link.project?.title ?? null });
      }}
      className="touch-manipulation rounded-md px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
      aria-label={t("intake_links.list.revoke_aria")}
    >
      {t("intake_links.list.revoke")}
    </button>
  );
}

function LoadingState() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white py-12 text-sm text-slate-400">
      <svg
        className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
        <path d="M14 8a6 6 0 0 1-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      {t("app.loading")}
    </div>
  );
}

function EmptyState({
  filtered,
  onCreate,
  onClearFilter,
}: {
  filtered: boolean;
  onCreate: () => void;
  onClearFilter: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
      <svg
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="mb-3 text-slate-300"
      >
        <path
          d="M9 11l3 3 7-7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20 12v6a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <h2 className="text-base font-semibold text-slate-900">
        {filtered
          ? t("intake_links.list.empty_filtered.title")
          : t("intake_links.list.empty.title")}
      </h2>
      <p className="mt-1 max-w-md text-sm text-slate-500">
        {filtered
          ? t("intake_links.list.empty_filtered.subtitle")
          : t("intake_links.list.empty.subtitle")}
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {filtered ? (
          <button
            type="button"
            onClick={onClearFilter}
            className="text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500 touch-manipulation rounded-md px-4 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("intake_links.list.empty_filtered.clear")}
          </button>
        ) : (
          <button
            type="button"
            onClick={onCreate}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-md px-4 py-2 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("intake_links.list.empty.cta")}
          </button>
        )}
      </div>
    </div>
  );
}

function formatDate(iso: string, lang: "fr" | "en"): string {
  try {
    return new Intl.DateTimeFormat(lang === "fr" ? "fr-CA" : "en-CA", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function formatSubmissions(link: IntakeLink): string {
  if (link.max_submissions === null) {
    return `${link.submission_count}\u00a0/\u00a0∞`;
  }
  return `${link.submission_count}\u00a0/\u00a0${link.max_submissions}`;
}

function LinkIdentity({ link }: { link: IntakeLink }) {
  const { t } = useTranslation();
  return (
    <div className="mt-1 space-y-1 break-words text-sm text-slate-600">
      {link.census_upload_id ? (
        <p>{t("intake_links.detail.census_upload_short")}</p>
      ) : (
        link.form && (
          <p>
            {link.form.supports_client_signature
              ? t("flow_clarity.mandate")
              : t("flow_clarity.form")}{" "}
            · {link.form.name} · {t("request_flow.version", { number: link.form.version_number })}
          </p>
        )
      )}
      <p>{link.contact?.name ?? t("flow_clarity.contact_unknown")}</p>
    </div>
  );
}
